import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { X } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

/**
 * Admin-controlled ad slot. Fetches /api/ads?placement=... and renders the first active ad.
 * Supports image+link ads and raw HTML ads. Silent-renders nothing when no ad exists.
 */
export default function AdSlot({ placement, className = '', showLabel = true }) {
  const [ad, setAd] = useState(null);
  const [dismissed, setDismissed] = useState(false);

  useEffect(() => {
    if (!placement) return;
    let active = true;
    axios.get(`${API}/ads?placement=${encodeURIComponent(placement)}`)
      .then(r => {
        if (!active) return;
        const list = r.data.ads || [];
        if (list.length > 0) {
          // random rotation across active ads for this slot
          setAd(list[Math.floor(Math.random() * list.length)]);
        }
      })
      .catch(() => {});
    return () => { active = false; };
  }, [placement]);

  if (!ad || dismissed) return null;

  const handleClick = () => {
    axios.post(`${API}/ads/${ad._id}/click`).catch(() => {});
  };

  const imgSrc = ad.image_url
    ? (ad.image_url.startsWith('http') ? ad.image_url : `${API}/files/${ad.image_url}`)
    : '';

  const inner = ad.html_content ? (
    <div className="w-full" dangerouslySetInnerHTML={{ __html: ad.html_content }} />
  ) : (
    <img src={imgSrc} alt={ad.title} className="w-full h-auto object-cover" />
  );

  const content = ad.link_url ? (
    <a
      href={ad.link_url}
      target="_blank"
      rel="noopener noreferrer sponsored"
      onClick={handleClick}
      className="block w-full"
      data-testid={`ad-link-${ad._id}`}
    >
      {inner}
    </a>
  ) : (
    <div className="w-full">{inner}</div>
  );

  return (
    <aside
      data-testid={`ad-slot-${placement}`}
      data-ad-id={ad._id}
      className={`relative group rounded-2xl overflow-hidden border border-slate-100 bg-white shadow-sm ${className}`}
    >
      {showLabel && (
        <span className="absolute top-2 left-2 z-10 text-[9px] uppercase tracking-wider bg-black/50 text-white px-1.5 py-0.5 rounded-full font-semibold">
          Reklam
        </span>
      )}
      <button
        type="button"
        onClick={() => setDismissed(true)}
        className="absolute top-2 right-2 z-10 w-5 h-5 rounded-full bg-black/40 hover:bg-black/60 text-white flex items-center justify-center opacity-0 group-hover:opacity-100 transition"
        aria-label="Reklamı kapat"
        data-testid={`ad-dismiss-${placement}`}
      >
        <X className="w-3 h-3" />
      </button>
      {content}
    </aside>
  );
}
